import type {
  ControllerUIInputFrame,
  ControllerUIRouteResult,
} from '../../../systems/input/controllerUIInputBridge';

export type ControllerNavigationDirection = 'up' | 'down' | 'left' | 'right';

export const CONTROLLER_DIRECTION_REPEAT_INITIAL_DELAY_MS = 360;
export const CONTROLLER_DIRECTION_REPEAT_CADENCE_MS = 110;

export interface ControllerNavigationActions {
  isCaptureActive(): boolean;
  toggleQuickMenu(): void;
  moveFocus(direction: ControllerNavigationDirection): void;
  confirm(): void;
  back(): void;
  allowContextualQuestAction(): boolean;
}

function heldDirection(frame: ControllerUIInputFrame): ControllerNavigationDirection | null {
  if (frame.up && !frame.down) return 'up';
  if (frame.down && !frame.up) return 'down';
  if (frame.left && !frame.right) return 'left';
  if (frame.right && !frame.left) return 'right';
  return null;
}

export class ControllerNavigationEngine {
  private direction: ControllerNavigationDirection | null = null;
  private nextRepeatAt = 0;

  public route(
    frame: ControllerUIInputFrame,
    actions: ControllerNavigationActions,
  ): ControllerUIRouteResult {
    if (!frame.connected) {
      this.reset();
      return { captureGamepad: false, allowContextualQuestAction: false };
    }

    if (frame.menuJustPressed) {
      actions.toggleQuickMenu();
      this.reset();
      return {
        captureGamepad: true,
        allowContextualQuestAction: actions.allowContextualQuestAction(),
      };
    }

    if (!actions.isCaptureActive()) {
      this.reset();
      return { captureGamepad: false, allowContextualQuestAction: false };
    }

    if (frame.backJustPressed) {
      actions.back();
      this.reset();
      return {
        captureGamepad: true,
        allowContextualQuestAction: actions.allowContextualQuestAction(),
      };
    }

    const nextDirection = heldDirection(frame);
    if (!nextDirection) {
      this.reset();
    } else if (nextDirection !== this.direction) {
      this.direction = nextDirection;
      this.nextRepeatAt = frame.timestamp + CONTROLLER_DIRECTION_REPEAT_INITIAL_DELAY_MS;
      actions.moveFocus(nextDirection);
    } else if (frame.timestamp >= this.nextRepeatAt) {
      this.nextRepeatAt = frame.timestamp + CONTROLLER_DIRECTION_REPEAT_CADENCE_MS;
      actions.moveFocus(nextDirection);
    }

    if (frame.confirmJustPressed) actions.confirm();

    return {
      captureGamepad: true,
      allowContextualQuestAction: actions.allowContextualQuestAction(),
    };
  }

  public reset(): void {
    this.direction = null;
    this.nextRepeatAt = 0;
  }
}
